import React, { useEffect } from 'react';
import { Dispatch, SetStateAction, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { DepositStepsEnum } from './types';
import { Button, ErrorToast } from '../../../components';
import { SelectedAddress } from '../../../styles/stylesStyled';
import { InputBackOffice } from '../../../components/common/InputBackoffice';
import { CaptionText, P1, P2, P3 } from '../../../styles/FontStyles';
import { colors } from '../../../styles/GlobalStyles';
import { Flexed, SelectedUSDT } from '../../../styles/GlobalComponents';
import { TokensType, WalletsType } from '../../../types/types';
import { copyLink, formatUSD } from '../../../helpers/helperFunctions';
import { useTranslation } from 'react-i18next';
import walletImage from '../../../assets/icons/backOffice/Packages/wallet.svg';
import { depositAkasha } from '../../Marketplace/Marketplace.actions';
import { useParams } from 'react-router-dom';
import Spinner from '../../../components/common/Spinner';
import usdtIcon from '../../../assets/icons/backOffice/USDT.svg';
import wULXIcon from '../../../assets/icons/backOffice/ULX.svg';
import akashaIcon from '../../../assets/icons/backOffice/AKASHA.svg';
import { SelectNetwork } from './SelectNetwork/SelectNetwork';
import { ethers, Contract } from 'ethers';
import erc20Abi from '../../../assets/abi/erc20.json';

interface Step1Props {
  setDepositStep: Dispatch<SetStateAction<DepositStepsEnum>>;
  wallet: string;
  token: TokensType;
  ulx: string;
  usdt: string;
  setUlx: Dispatch<SetStateAction<string>>;
  setUsdt: Dispatch<SetStateAction<string>>;
  selectedNetwork: WalletsType;
  setSelectedNetwork: Dispatch<SetStateAction<WalletsType>>;
}

export const Step1 = ({
  setDepositStep,
  wallet,
  token,
  ulx,
  usdt,
  setUlx,
  setUsdt,
  selectedNetwork,
  setSelectedNetwork
}: Step1Props) => {
  const NODE_URL = 'https://sepolia.base.org';
  const CONTRACT_ADDRESS = '0xd016ea99Fc64B5f93a877a3316d18c0cCebd79E9';

  const [baseAkashaBalance, setBaseAkashaBalance] = useState<any>(0);

  const dispatch = useAppDispatch();

  const { t } = useTranslation('packages');

  const { userPackage, exchangeRates, walletSummary, loading } = useAppSelector(
    (state) => state.marketplace
  );

  const getBalance = async () => {
    try {
      const provider = new ethers.providers.JsonRpcProvider(NODE_URL);
      const contract = new Contract(CONTRACT_ADDRESS, erc20Abi, provider);

      const rawBalance = await contract.balanceOf(walletSummary?.userAddress);
      const decimals = process.env.REACT_APP_AKASHA_DECIMAL;

      const formattedBalance = ethers.utils.formatUnits(rawBalance, decimals);

      const parsedBalance = parseFloat(formattedBalance);
      const balance = isNaN(parsedBalance) ? 0 : Math.trunc(parsedBalance);

      setBaseAkashaBalance(balance);
    } catch (error) {
      ErrorToast('Error fetching balance');
    }
  };

  const { id } = useParams<{ id: string }>();

  const handleDeposit = () => {
    if (
      ulx >
      (token.token === 'Ultron (ULX)'
        ? walletSummary?.availableBalanceUlx
        : walletSummary?.availableBalanceAkasha)
    ) {
      ErrorToast('You don’t have enough balance.');
    } else {
      dispatch(
        depositAkasha({
          userPackageId: Number(id),
          amount: Number(ulx),
          token: token.value,
          // chain: selectedNetwork?.networkType === 4 ? 'base' : 'ultron',
          chain: 'ultron',
          getBalance: getBalance,
          setStep: setDepositStep
        })
      );
    }
  };

  const handleUlxChange = (e: any) => {
    const val = e;
    setUlx(val);

    if (!isNaN(parseFloat(val))) {
      const result =
        parseFloat(val) *
        (token.token === 'Ultron (ULX)'
          ? exchangeRates?.ulxExchangeRate
          : exchangeRates?.akashaExchangeRate);

      setUsdt(result.toFixed(2));
    } else {
      setUsdt('');
    }
  };

  const handleUsdtChange = (e: any) => {
    const val = e;
    setUsdt(val);

    if (!isNaN(parseFloat(val))) {
      const result =
        parseFloat(val) /
        (token.token === 'Ultron (ULX)'
          ? exchangeRates?.ulxExchangeRate
          : exchangeRates?.akashaExchangeRate);

      setUlx(result.toFixed(2));
    } else {
      setUlx('');
    }
  };

  const calculateDepositAmountRemaining = (
    maxDepositAmount: number,
    depositedAmount: number
  ) => {
    const depositedAmountRemaining = maxDepositAmount - depositedAmount;

    const truncated = depositedAmountRemaining;

    const newTruncated = Math.floor(truncated * 100) / 100;

    return newTruncated;
  };

  const calculatewULXExchangeRate = (depositedAmount: number) => {
    const exchange =
      depositedAmount /
      (token.token === 'Ultron (ULX)'
        ? exchangeRates?.ulxExchangeRate
        : exchangeRates?.akashaExchangeRate);
    return formatUSD(exchange);
  };

  // const calculatewUSDTExchangeRate = () => {
  //   const exchange =
  //     (token.token === 'Ultron (ULX)'
  //       ? walletSummary?.availableBalanceUlx
  //       : walletSummary?.availableBalanceAkasha) *
  //     (token.token === 'Ultron (ULX)'
  //       ? exchangeRates?.ulxExchangeRate
  //       : exchangeRates?.akashaExchangeRate);
  //   return formatUSD(exchange);
  // };

  const calculatewUSDTExchangeRate = () => {
    const exchange =
      (token.token === 'Ultron (ULX)'
        ? walletSummary?.availableBalanceUlx
        : selectedNetwork?.networkType === 3
        ? walletSummary?.availableBalanceAkasha
        : baseAkashaBalance) *
      (token.token === 'Ultron (ULX)'
        ? exchangeRates?.ulxExchangeRate
        : exchangeRates?.akashaExchangeRate);
    return formatUSD(exchange);
  };

  const checkMaxAmount = () => {
    const maxDeposit = userPackage?.package?.maxDepositAmount ?? 0;
    const depositedAmount = userPackage?.depositedAmount ?? 0;

    const depositedAmountRemaining = maxDeposit - depositedAmount;

    if (Number(usdt) > depositedAmountRemaining) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <>
          {' '}
          <P2 center mb='20' color={colors.neutral500}>
            NODE ID #{id}
          </P2>
          <P3 bold mb='8'>
            My Back Office Wallet Address
          </P3>
          <SelectedUSDT
            onClick={() => {
              copyLink(wallet, t('waiting.copied'));
            }}
            style={{ cursor: 'pointer' }}>
            <div className='truncated'>
              <div>{wallet}</div>
            </div>

            <img
              src={walletImage}
              alt='withdraw'
              style={{ width: 16, marginLeft: 10 }}
            />
          </SelectedUSDT>
          <CaptionText color={colors.error500} mb='20' mt='10'>
            Please make sure your connected wallet has enough{' '}
            {`${token.token === 'Ultron (ULX)' ? 'ULX' : 'AK1111'}`} to complete
            the deposit. If not, click here to watch the tutorial on how to get{' '}
            {`${token.token === 'Ultron (ULX)' ? 'ULX' : 'AK1111'}`}.
          </CaptionText>
          <P3 bold mb='8'>
            Node Type
          </P3>
          <InputBackOffice disabled value={userPackage?.package?.title} />
          <CaptionText color={colors.neutral500} mb='20'>
            Basic Node Max Deposit Amount:{' '}
            {formatUSD(userPackage?.package?.maxDepositAmount)} USDT
          </CaptionText>
          <P3 bold mb='8'>
            Coin Selected
          </P3>
          <InputBackOffice disabled value={token.token} />
          {token.token === 'Akasha (AK1111)' && (
            <>
              {' '}
              <P3 bold mb='8'>
                Blockchain
              </P3>
              <SelectNetwork
                onChange={(n) => {
                  setSelectedNetwork(n);
                  n?.networkType === 4 && getBalance();
                }}
                value={selectedNetwork}
              />
              {selectedNetwork?.networkType === 4 && (
                <CaptionText color={colors.neutral500} mb='20'>
                  Deposit fee 0.6%
                </CaptionText>
              )}
            </>
          )}
          <P3 bold mb='8'>
            Deposit Amount Remaining
          </P3>
          <InputBackOffice
            disabled
            value={calculateDepositAmountRemaining(
              userPackage?.package?.maxDepositAmount ?? 0,
              userPackage?.depositedAmount ?? 0
            )}
            icon={usdtIcon}
          />
          <CaptionText color={colors.neutral500} mb='20'>
            Current Deposited Amount: {formatUSD(userPackage?.depositedAmount)}{' '}
            USDT /{' '}
            {calculatewULXExchangeRate(userPackage?.depositedAmount ?? 0)}{' '}
            {`${token.token === 'Ultron (ULX)' ? 'ULX' : 'AK1111'}`}
          </CaptionText>
          <Flexed between center mb='8'>
            <P3 bold>Deposit Amount</P3>
            <Button
              textButton
              w='auto'
              h='auto'
              onClick={() => setDepositStep(DepositStepsEnum.SELECT_PAYMENT)}>
              Add {`${token.token === 'Ultron (ULX)' ? 'wULX' : 'AK1111'}`}
            </Button>
          </Flexed>
          <Flexed gap='10px'>
            <InputBackOffice
              value={ulx}
              type='number'
              onChange={handleUlxChange}
              icon={`${token.token === 'Ultron (ULX)' ? wULXIcon : akashaIcon}`}
            />
            <InputBackOffice
              type='number'
              value={usdt}
              onChange={handleUsdtChange}
              icon={usdtIcon}
            />
          </Flexed>
          <Flexed between>
            <CaptionText color={colors.neutral500} mb='20'>
              {token.token === 'Ultron (ULX)' ? 'wULX' : 'AK1111'} current
              balance:{' '}
              {token.token === 'Ultron (ULX)'
                ? formatUSD(walletSummary?.availableBalanceUlx)
                : selectedNetwork?.networkType === 3
                ? formatUSD(walletSummary?.availableBalanceAkasha)
                : formatUSD(baseAkashaBalance)}{' '}
              / {calculatewUSDTExchangeRate()} USDT
            </CaptionText>
            {selectedNetwork?.networkType === 4 && (
              <CaptionText color={colors.neutral500} mb='20'>
                Deposit fee 0.6%
              </CaptionText>
            )}
          </Flexed>
          <CaptionText color={colors.error500} mb='20' bold>
            Due to market price fluctuations, the value of your deposit may
            change over time. These changes are natural to market dynamics and
            cannot be controlled or guaranteed.
          </CaptionText>
          <Button
            mt='20'
            ml='10'
            onClick={handleDeposit}
            disabled={ulx === '' || checkMaxAmount()}>
            Deposit
          </Button>
        </>
      )}
    </>
  );
};
